<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { CircleHelp } from 'lucide-vue-next'
import SupportChatModal from '@/Components/SupportChatModal.vue'
import { SUPPORT_EVENT } from '@/composables/useSupport'

/**
 * Help, in the bottom-right corner of every page — marketing and dashboard
 * alike.
 *
 * It used to be a floating button on the public pages and a link buried at the
 * foot of the dashboard sidebar, so someone who learned where help was before
 * signing up could not find it afterwards. One component, one position.
 *
 * Bottom left. The right-hand corner is where a page's own primary action ends
 * up — a subscribe button, a sticky footer bar — and a floating button sitting
 * on top of it is the one thing a visitor must never have to move around.
 */
const open = ref(false)

/**
 * Any page can ask for this form. It replaces the `mailto:` links that used to
 * stand in for support in half a dozen places — see `useSupport`.
 */
const show = () => (open.value = true)

onMounted(() => window.addEventListener(SUPPORT_EVENT, show))
onBeforeUnmount(() => window.removeEventListener(SUPPORT_EVENT, show))
</script>

<template>
  <button
    v-if="!open"
    type="button"
    aria-label="Help and support"
    class="fixed bottom-5 left-5 z-40 flex items-center gap-2 rounded-full bg-brand px-4 py-3 text-brand-contrast shadow-md transition-[background-color,transform] duration-150 ease-brand hover:bg-brand-hover hover:-translate-y-0.5"
    @click="open = true"
  >
    <CircleHelp class="size-5" />
    <span class="hidden text-sm font-semibold sm:inline">Help</span>
  </button>

  <SupportChatModal :open="open" @close="open = false" />
</template>
